/**
 * ShareViaChatModal
 * Lets any user pick one or more team members and send a report/thread
 * as a structured DM message in the internal chat.
 */
import { useState, useEffect } from 'react';
import {
  getFirestore, collection, addDoc, onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { createNotification } from '../../notifUtils';

const db = () => getFirestore(getApp());

// Deterministic DM channel id — same as ChatPage
function dmChatId(a, b) { return [a, b].sort().join('_'); }

const ROLE_COLOR = {
  admin:'#ef4444', ops:'#e11d48', mp:'#6366f1',
  embedded:'#f59e0b', system:'#10b981', pe:'#8b5cf6',
  obc:'#00FFB2', experimental:'#06b6d4', finance:'#10b981',
  mentor:'#7c3aed', employee:'#64748b', guest:'#475569',
};

export default function ShareViaChatModal({
  post,          // Firestore post object
  portalId,      // e.g. 'mp'
  portalLabel,   // e.g. 'Mission Planning'
  portalColor,   // e.g. '#6366f1'
  portalPath,    // e.g. '/mission_planning'
  currentUser,   // { uid, name, role }
  onClose,
}) {
  const [members,  setMembers]  = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search,   setSearch]   = useState('');
  const [message,  setMessage]  = useState('');
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db(), 'authorized_users'),
      snap => {
        const list = snap.docs
          .map(d => ({ uid: d.id, ...d.data() }))
          .filter(m => m.active !== false && m.uid !== currentUser?.uid);
        setMembers(list);
      },
      () => {}
    );
    return unsub;
  }, [currentUser?.uid]);

  function toggle(uid) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  }

  async function handleSend() {
    if (!selected.size || sending) return;
    setSending(true);

    const title = post.title || post.text?.slice(0, 80) || 'Untitled report';

    for (const uid of selected) {
      const chatId = dmChatId(currentUser.uid, uid);
      const msgDoc = {
        senderId:    currentUser.uid,
        senderName:  currentUser.name,
        senderRole:  currentUser.role,
        createdAt:   serverTimestamp(),
        delivered:   false,
        readAt:      null,
        encrypted:   false,
        type:        'report_share',
        reportId:    post.id,
        reportTitle: title,
        portalId:    portalId   || '',
        portalLabel: portalLabel || '',
        portalColor: portalColor || '#0e9cc0',
        portalPath:  portalPath  || '/',
        shareNote:   message.trim() || null,
      };
      try {
        await addDoc(collection(db(), 'chats', chatId, 'messages'), msgDoc);
        await createNotification({
          recipientId: uid,
          type:        'message',
          title:       `📎 ${currentUser.name} shared a report`,
          body:        title,
          fromId:      currentUser.uid,
          fromName:    currentUser.name,
          link:        'chat',
        });
      } catch (_) {}
    }

    setSending(false);
    setSent(true);
    setTimeout(onClose, 1200);
  }

  const S = {
    overlay: {
      position: 'fixed', inset: 0, zIndex: 9500,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    },
    modal: {
      background: '#0d1117', border: '1px solid #1e293b',
      borderRadius: 14, width: '100%', maxWidth: 480,
      maxHeight: '80vh', display: 'flex', flexDirection: 'column',
      boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
      overflow: 'hidden',
    },
    header: {
      padding: '18px 20px 14px', borderBottom: '1px solid #1e293b',
      display: 'flex', alignItems: 'flex-start', gap: 12,
    },
    mono: { fontFamily: "'Share Tech Mono', monospace" },
  };

  if (sent) {
    return (
      <div style={S.overlay}>
        <div style={{ ...S.modal, alignItems: 'center', justifyContent: 'center', padding: 48, gap: 16 }}>
          <div style={{ fontSize: 36 }}>✅</div>
          <div style={{ ...S.mono, fontSize: 13, color: '#10b981', letterSpacing: 1 }}>
            Shared with {selected.size} member{selected.size !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="smc-modal-overlay" style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="smc-modal-panel" style={S.modal}>

        {/* Header */}
        <div style={S.header}>
          <div style={{ flex: 1 }}>
            <div style={{ ...S.mono, fontSize: 9, letterSpacing: 2, color: portalColor || '#0e9cc0', marginBottom: 4 }}>
              SHARE VIA CHAT · {(portalLabel || '').toUpperCase()}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'min(360px, 100%)' }}>
              {post.title || post.text?.slice(0, 60) || 'Untitled'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid #1e293b', borderRadius: 6, width: 28, height: 28, cursor: 'pointer', color: '#64748b', fontSize: 14, flexShrink: 0 }}>✕</button>
        </div>

        {/* Report preview chip */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #1e293b' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: `${portalColor || '#0e9cc0'}12`, border: `1px solid ${portalColor || '#0e9cc0'}33`, borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: portalColor || '#0e9cc0', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...S.mono, fontSize: 8, letterSpacing: 1, color: portalColor || '#0e9cc0', marginBottom: 3 }}>
                {(portalLabel || '').toUpperCase()} · REPORT
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {post.title || post.text?.slice(0, 60) || 'Untitled'}
              </div>
            </div>
          </div>
        </div>

        {/* Member list header + search */}
        <div style={{ padding: '12px 20px 8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ ...S.mono, fontSize: 9, letterSpacing: 2, color: '#64748b' }}>
              SELECT RECIPIENTS ({selected.size} selected)
            </div>
            {selected.size > 0 && (
              <button onClick={() => setSelected(new Set())} style={{
                ...S.mono, fontSize: 8, letterSpacing: 1, padding: '2px 8px', cursor: 'pointer',
                background: 'transparent', border: '1px solid #334155', color: '#64748b', borderRadius: 4,
              }}>CLEAR</button>
            )}
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or role…"
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 7,
              background: '#111827', border: '1px solid #1e293b',
              color: '#e2e8f0', fontFamily: 'system-ui, sans-serif', fontSize: 12,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '0 20px' }}>
          {members.length === 0 && (
            <div style={{ ...S.mono, fontSize: 10, color: '#64748b', padding: '20px 0', textAlign: 'center' }}>
              Loading team members…
            </div>
          )}
          {(() => {
            const filtered = members.filter(m => {
              if (!search.trim()) return true;
              const q = search.toLowerCase();
              return (m.name||'').toLowerCase().includes(q) || (m.role||'').toLowerCase().includes(q) || (m.email||'').toLowerCase().includes(q);
            });
            if (search.trim() && filtered.length === 0) return (
              <div style={{ ...S.mono, fontSize: 10, color: '#64748b', padding: '16px 0', textAlign: 'center' }}>
                No members match "{search}"
              </div>
            );
            return filtered.map(m => {
            const isSel = selected.has(m.uid);
            const rc = ROLE_COLOR[m.role] || '#64748b';
            return (
              <div
                key={m.uid}
                onClick={() => toggle(m.uid)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px', marginBottom: 6, borderRadius: 8, cursor: 'pointer',
                  background: isSel ? `${portalColor || '#0e9cc0'}12` : '#111827',
                  border: `1px solid ${isSel ? (portalColor || '#0e9cc0') + '55' : '#1e293b'}`,
                  transition: 'all 0.12s',
                }}
              >
                {/* Checkbox */}
                <div style={{
                  width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                  border: `2px solid ${isSel ? (portalColor || '#0e9cc0') : '#334155'}`,
                  background: isSel ? (portalColor || '#0e9cc0') : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.12s',
                }}>
                  {isSel && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
                </div>
                {/* Avatar */}
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: `${rc}18`, border: `1px solid ${rc}44`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, color: rc, fontFamily: S.mono.fontFamily,
                }}>
                  {(m.name || m.email || '?')[0].toUpperCase()}
                </div>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.name || m.email?.split('@')[0] || '—'}
                  </div>
                  <div style={{ ...S.mono, fontSize: 8, letterSpacing: 1, color: rc, marginTop: 2 }}>
                    {(m.role || 'MEMBER').toUpperCase()}
                  </div>
                </div>
              </div>
            );
          });
          })()}
        </div>

        {/* Optional note */}
        <div style={{ padding: '12px 20px 0' }}>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Add a note (optional)…"
            rows={2}
            style={{
              width: '100%', background: '#111827', border: '1px solid #1e293b',
              borderRadius: 8, padding: '9px 12px', color: '#e2e8f0',
              fontFamily: 'system-ui, sans-serif', fontSize: 13, outline: 'none',
              resize: 'none', boxSizing: 'border-box', lineHeight: 1.5,
            }}
          />
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px 18px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 8, fontFamily: S.mono.fontFamily, fontSize: 10, letterSpacing: 1, cursor: 'pointer', background: 'transparent', border: '1px solid #1e293b', color: '#64748b' }}>
            CANCEL
          </button>
          <button
            onClick={handleSend}
            disabled={!selected.size || sending}
            style={{
              padding: '9px 24px', borderRadius: 8, fontFamily: S.mono.fontFamily, fontSize: 10,
              letterSpacing: 1, cursor: selected.size && !sending ? 'pointer' : 'not-allowed',
              background: selected.size && !sending ? (portalColor || '#0e9cc0') : '#1e293b',
              border: `1px solid ${selected.size && !sending ? (portalColor || '#0e9cc0') : '#334155'}`,
              color: selected.size && !sending ? '#fff' : '#475569',
              transition: 'all 0.15s', fontWeight: 600,
            }}
          >
            {sending ? 'SENDING…' : `SEND TO ${selected.size || '—'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
